import jsPDF from 'jspdf'
import { FormFieldType } from '../../enums'
import { FormRow, getFieldKey } from '../../generated-types/form-field'
import { getFormattedDate } from '../../utils'
import { UserDetail, getUserDetail } from '../../utils/getUserDetails'
import { parseHtmlToBlocks, renderLinkLine, renderRichTextBlocks, richTextLineCount } from 'src/utils/richTextPdfUtils'

const capitalizeFirst = (str: string) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : '')

const PDF_MARGIN = 14
const PDF_PAGE_WIDTH = 210
const PDF_PAGE_HEIGHT = 297
const PDF_CONTENT_WIDTH = PDF_PAGE_WIDTH - PDF_MARGIN * 2
const PDF_LINE_HEIGHT = 5
const PDF_FONT_TITLE = 16
const PDF_FONT_SUBTITLE = 11
const PDF_FONT_LABEL = 8
const PDF_FONT_VALUE = 10
const PDF_FIELD_PADDING = 1
const PDF_LABEL_VALUE_GAP = 1.5
const PDF_SECTION_GAP = 5
const PDF_COL_GAP = 6
const PDF_COL_PADDING = 2
const PDF_ROW_GAP = 3

const formatFieldValueForPDF = (field, value, t, getFormattedDate) => {
    if (value == null || value === '') return '—'
    switch (field.type) {
        case FormFieldType.datePicker:
            return getFormattedDate(new Date(value))
        case FormFieldType.dateTime:
            return getFormattedDate(new Date(value), 'YYYY-MM-DD HH:mm A')
        case FormFieldType.checkBox:
            return value ? t('yes', { ns: 'common' }) : t('no', { ns: 'common' })
        case FormFieldType.checkBoxList: {
            try {
                const list = JSON.parse(value || '[]')
                return Array.isArray(list) ? list.join(', ') : value
            } catch {
                return value
            }
        }
        case FormFieldType.fileUpload:
            return value ? `${t('uploaded-file-link', { ns: 'overall-reports' })}: ${value}` : '—'
        default:
            return value
    }
}

const instructionTypes = [FormFieldType.richText, FormFieldType.instructionImage, FormFieldType.instructionFile]

const addPageIfNeeded = (doc, requiredSpace, yRef: { current: number }) => {
    if (yRef.current + requiredSpace > PDF_PAGE_HEIGHT - PDF_MARGIN) {
        doc.addPage()
        yRef.current = PDF_MARGIN
    }
}

export const exportSchemaReportPDF = (
    report,
    layouts: FormRow[],
    t,
    options?: {
        schema?: { name: string; description?: string }
        createdAt?: string
        createdByUser?: { fullName?: string; firstName?: string; lastName?: string }
        linkedEntity?: string
        locality?: string
    }
) => {
    const doc = new jsPDF({ format: 'a4', unit: 'mm' })
    const values = JSON.parse(report.values || '{}')
    const yRef = { current: PDF_MARGIN }
    const getY = () => yRef.current
    const advanceY = (delta: number) => {
        yRef.current += delta
    }

    doc.setFont('helvetica', 'bold')
    doc.setFontSize(PDF_FONT_TITLE)
    doc.setTextColor(0, 0, 0)
    const schemaName = capitalizeFirst(options?.schema?.name || t('overall-reports', { ns: 'overall-reports' }))
    doc.text(schemaName, PDF_MARGIN, getY())
    advanceY(PDF_LINE_HEIGHT + 2)

    doc.setFont('helvetica', 'bold')
    doc.setFontSize(PDF_FONT_SUBTITLE)
    doc.text(t('details', { ns: 'common' }), PDF_MARGIN, getY())
    advanceY(PDF_LINE_HEIGHT + 1)

    const detailRows: [string, string][] = [
        ...(options?.schema?.description ? [[t('description', { ns: 'common' }), options.schema.description] as [string, string]] : []),
        ...(options?.linkedEntity ? [[t('linked-entity', { ns: 'common' }), options.linkedEntity] as [string, string]] : []),
        ...(options?.locality ? [[t('locality', { ns: 'common' }), options.locality] as [string, string]] : []),
        [t('filled-date', { ns: 'common' }), options?.createdAt ? getFormattedDate(new Date(options.createdAt), 'YYYY-MM-DD HH:mm:ss') : '—'],
        [t('createdBy', { ns: 'common' }), getUserDetail(options?.createdByUser, UserDetail.FullName)]
    ]
    const mid = Math.ceil(detailRows.length / 2)
    const leftRows = detailRows.slice(0, mid)
    const rightRows = detailRows.slice(mid)
    const colWidth = (PDF_CONTENT_WIDTH - PDF_COL_GAP) / 2

    const writeDetailRow = (label: string, value: string, x: number, y: number) => {
        doc.setFont('helvetica', 'normal')
        doc.setFontSize(PDF_FONT_LABEL)
        doc.setTextColor(100, 100, 100)
        doc.text(label, x, y)
        doc.setFontSize(PDF_FONT_VALUE)
        doc.setTextColor(0, 0, 0)
        const valueLines = doc.splitTextToSize(value, colWidth)
        valueLines.forEach((line, i) => {
            doc.text(line, x, y + PDF_LINE_HEIGHT + i * PDF_LINE_HEIGHT)
        })
        return PDF_LINE_HEIGHT + valueLines.length * PDF_LINE_HEIGHT + 1
    }

    const detailsStartY = getY()
    let leftY = detailsStartY
    let rightY = detailsStartY
    const leftX = PDF_MARGIN
    const rightX = PDF_MARGIN + colWidth + PDF_COL_GAP

    const totalDetailsHeight = Math.max(
        leftRows.reduce((sum, [, v]) => sum + PDF_LINE_HEIGHT + doc.splitTextToSize(v, colWidth).length * PDF_LINE_HEIGHT + 1, 0),
        rightRows.reduce((sum, [, v]) => sum + PDF_LINE_HEIGHT + doc.splitTextToSize(v, colWidth).length * PDF_LINE_HEIGHT + 1, 0)
    )
    addPageIfNeeded(doc, totalDetailsHeight + 6, yRef)
    const startY = getY()
    leftY = startY
    rightY = startY

    doc.setFillColor(240, 248, 251)
    doc.setDrawColor(230, 235, 240)
    doc.rect(PDF_MARGIN - 2, startY - 3, PDF_CONTENT_WIDTH + 4, totalDetailsHeight + 6, 'FD')

    leftRows.forEach(([label, value]) => {
        const h = writeDetailRow(label, value, leftX, leftY)
        leftY += h
    })
    rightRows.forEach(([label, value]) => {
        const h = writeDetailRow(label, value, rightX, rightY)
        rightY += h
    })
    yRef.current = Math.max(leftY, rightY)

    advanceY(PDF_SECTION_GAP)
    addPageIfNeeded(doc, PDF_LINE_HEIGHT + 2, yRef)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(PDF_FONT_SUBTITLE)
    doc.text(t('form-data', { ns: 'common' }), PDF_MARGIN, getY())
    advanceY(PDF_LINE_HEIGHT + 1)

    const renderFormField = (field, rawValue, x, colWidth, y) => {
        const textWidth = Math.max(colWidth - PDF_COL_PADDING * 2, 10)
        const displayValue = field.type === FormFieldType.title ? '' : formatFieldValueForPDF(field, rawValue, t, getFormattedDate)
        const valueLines = field.type !== FormFieldType.title ? doc.splitTextToSize(String(displayValue), textWidth) : []
        const labelLines = doc.splitTextToSize(field.name, textWidth)
        const blockHeight = field.type === FormFieldType.title
            ? labelLines.length * PDF_LINE_HEIGHT + PDF_FIELD_PADDING
            : labelLines.length * PDF_LINE_HEIGHT + PDF_LABEL_VALUE_GAP + valueLines.length * PDF_LINE_HEIGHT + PDF_FIELD_PADDING

        if (field.type === FormFieldType.title) {
            doc.setFont('helvetica', 'bold')
            doc.setFontSize(PDF_FONT_SUBTITLE)
            doc.setTextColor(0, 0, 0)
            labelLines.forEach((line, i) => {
                doc.text(line, x + PDF_COL_PADDING, y + i * PDF_LINE_HEIGHT)
            })
        } else {
            doc.setFont('helvetica', 'normal')
            doc.setFontSize(PDF_FONT_LABEL)
            doc.setTextColor(100, 100, 100)
            labelLines.forEach((line, i) => {
                doc.text(line, x + PDF_COL_PADDING, y + i * PDF_LINE_HEIGHT)
            })
            doc.setFontSize(PDF_FONT_VALUE)
            doc.setTextColor(0, 0, 0)
            valueLines.forEach((line, i) => {
                doc.text(line, x + PDF_COL_PADDING, y + labelLines.length * PDF_LINE_HEIGHT + PDF_LABEL_VALUE_GAP + i * PDF_LINE_HEIGHT)
            })
        }
        return blockHeight
    }

    layouts.forEach((row) => {
        const columns = row.columns || []
        const colCount = Math.max(columns.length, 1)
        const colWidth = (PDF_CONTENT_WIDTH - (colCount - 1) * PDF_COL_GAP) / colCount

        const textWidth = Math.max(colWidth - PDF_COL_PADDING * 2, 10)
        let rowHeight = 0
        columns.forEach((col) => {
            let colHeight = 0
                ; (col.fields || []).forEach((field) => {
                    if (instructionTypes.includes(field.type) && !field.showInReport) return
                    if (field.type === FormFieldType.richText) {
                        const blocks = parseHtmlToBlocks(field.content ?? '')
                        colHeight += richTextLineCount(doc, blocks, textWidth) * PDF_LINE_HEIGHT + PDF_FIELD_PADDING
                        return
                    }
                    if (field.type === FormFieldType.instructionImage || field.type === FormFieldType.instructionFile) {
                        colHeight += PDF_LINE_HEIGHT + PDF_FIELD_PADDING
                        return
                    }
                    const displayValue = field.type === FormFieldType.title
                        ? ''
                        : formatFieldValueForPDF(field, values[getFieldKey(field)] ?? values[field.name], t, getFormattedDate)
                    const valueLines = field.type !== FormFieldType.title ? doc.splitTextToSize(String(displayValue), textWidth) : []
                    const labelLines = doc.splitTextToSize(field.name, textWidth)
                    colHeight += field.type === FormFieldType.title
                        ? labelLines.length * PDF_LINE_HEIGHT + PDF_FIELD_PADDING
                        : labelLines.length * PDF_LINE_HEIGHT + PDF_LABEL_VALUE_GAP + valueLines.length * PDF_LINE_HEIGHT + PDF_FIELD_PADDING
                })
            rowHeight = Math.max(rowHeight, colHeight)
        })
        rowHeight += PDF_ROW_GAP
        addPageIfNeeded(doc, rowHeight, yRef)

        let rowStartY = getY()
        let colY = columns.map(() => rowStartY)

        columns.forEach((col, colIndex) => {
            const x = PDF_MARGIN + colIndex * (colWidth + PDF_COL_GAP)
                ; (col.fields || []).forEach((field) => {
                    if (instructionTypes.includes(field.type) && !field.showInReport) return
                    if (field.type === FormFieldType.richText) {
                        const blocks = parseHtmlToBlocks(field.content ?? '')
                        const blockHeight = richTextLineCount(doc, blocks, textWidth) * PDF_LINE_HEIGHT + PDF_FIELD_PADDING
                        if (colY[colIndex] + blockHeight > PDF_PAGE_HEIGHT - PDF_MARGIN) {
                            doc.addPage(); yRef.current = PDF_MARGIN; rowStartY = PDF_MARGIN; colY = columns.map(() => PDF_MARGIN)
                        }
                        renderRichTextBlocks(doc, blocks, x, textWidth, colY[colIndex])
                        colY[colIndex] += blockHeight
                        return
                    }
                    if (field.type === FormFieldType.instructionImage || field.type === FormFieldType.instructionFile) {
                        const linkText = field.type === FormFieldType.instructionFile ? (field.name || 'View file') : 'View image'
                        const blockHeight = PDF_LINE_HEIGHT + PDF_FIELD_PADDING
                        if (colY[colIndex] + blockHeight > PDF_PAGE_HEIGHT - PDF_MARGIN) {
                            doc.addPage(); yRef.current = PDF_MARGIN; rowStartY = PDF_MARGIN; colY = columns.map(() => PDF_MARGIN)
                        }
                        renderLinkLine(doc, linkText, field.content ?? '', x, colY[colIndex])
                        colY[colIndex] += blockHeight
                        return
                    }
                    const rawValue = values[getFieldKey(field)] ?? values[field.name]
                    const displayValue = field.type === FormFieldType.title ? '' : formatFieldValueForPDF(field, rawValue, t, getFormattedDate)
                    const valueLines = field.type !== FormFieldType.title ? doc.splitTextToSize(String(displayValue), textWidth) : []
                    const labelLines = doc.splitTextToSize(field.name, textWidth)
                    const blockHeight = field.type === FormFieldType.title
                        ? labelLines.length * PDF_LINE_HEIGHT + PDF_FIELD_PADDING
                        : labelLines.length * PDF_LINE_HEIGHT + PDF_LABEL_VALUE_GAP + valueLines.length * PDF_LINE_HEIGHT + PDF_FIELD_PADDING
                    if (colY[colIndex] + blockHeight > PDF_PAGE_HEIGHT - PDF_MARGIN) {
                        doc.addPage()
                        yRef.current = PDF_MARGIN
                        rowStartY = PDF_MARGIN
                        colY = columns.map(() => PDF_MARGIN)
                    }
                    renderFormField(field, rawValue, x, colWidth, colY[colIndex])
                    colY[colIndex] += blockHeight
                })
        })

        const maxColY = colY.length ? Math.max(...colY) : rowStartY
        yRef.current = maxColY + PDF_ROW_GAP
    })

    doc.save(`${new Date().toISOString().slice(0, 19).replace(/[:-]/g, '')}-${schemaName.replace(/\s/g, '-')}.pdf`)
}
